import { useAiToolkitContext } from '@app/(main)/(dark)/ai/_shared/_context/AiToolkitContext';
import {
    AI_TOOLKIT_CATEGORIES,
    AI_TOOLKIT_OPERATIONS,
    AiToolkitCategory,
    AiToolkitOperation,
} from '@app/(main)/(dark)/ai/_shared/_context/types';
import { usePathname, useRouter } from 'next/navigation';
import { type JSX, type ReactNode, useCallback } from 'react';

import { ConfirmationModal } from '@videoblocks/design-system';

import { pluralize } from '../../../../../../../utils/utils';
import { AI_TOOLKIT_TELEMETRY_ACTIONS, emitAiToolkitTag } from '../../_context/aiToolkitTelemetry';
import { getCreditCost } from '../../_context/getCreditCost';

const getModalContent = (
    category: AiToolkitCategory,
    calculatedCost: number,
    credits: number,
    operation?: AiToolkitOperation,
): ReactNode => {
    switch (category) {
        case AI_TOOLKIT_CATEGORIES.VIDEO_GENERATION:
            return (
                <div className="flex flex-col gap-4">
                    <span>{`You need ${calculatedCost} ${pluralize(calculatedCost, 'credit')} to generate this video.`}</span>
                    <span>{`Your balance is ${credits} ${pluralize(credits, 'credit')}.`}</span>
                </div>
            );
        case AI_TOOLKIT_CATEGORIES.IMAGE_GENERATION:
            return (
                <div className="flex flex-col gap-4">
                    <span>{`You need ${calculatedCost} ${pluralize(calculatedCost, 'credit')} to generate this image.`}</span>
                    <span>{`Your balance is ${credits} ${pluralize(credits, 'credit')}.`}</span>
                </div>
            );
        case AI_TOOLKIT_CATEGORIES.VIDEO_EDIT:
            return (
                <div className="flex flex-col gap-4">
                    <span>{`You need ${calculatedCost} ${pluralize(calculatedCost, 'credit')} to ${operation === AI_TOOLKIT_OPERATIONS.UPSCALE ? 'upscale' : 'edit'} this video.`}</span>
                    <span>{`Your balance is ${credits} ${pluralize(credits, 'credit')}.`}</span>
                </div>
            );
        case AI_TOOLKIT_CATEGORIES.VOICEOVER:
            return '';
    }
};

export const InsufficientCreditsModal = (): JSX.Element => {
    const { confirmationModal, dispatchConfirmationModal, credits, formStates, submitStates, subscriptionId } =
        useAiToolkitContext();
    const pathname = usePathname();
    const router = useRouter();

    const { type, category = AI_TOOLKIT_CATEGORIES.VIDEO_GENERATION } = confirmationModal ?? {};
    const isOpen = type === 'insufficientCredits' && confirmationModal?.isOpen === true;

    const onClose = useCallback((): void => {
        dispatchConfirmationModal({ type: 'CLOSE_MODAL' });
    }, [dispatchConfirmationModal]);

    const handleGetCredits = useCallback((): void => {
        emitAiToolkitTag(category, AI_TOOLKIT_TELEMETRY_ACTIONS.purchaseCredits);
        dispatchConfirmationModal({ type: 'CLOSE_MODAL' });
        const redirect = encodeURIComponent(pathname);
        const redirectPath = subscriptionId
            ? `/video/upgrade-my-plan/${subscriptionId}?redirectAfterRegister=${redirect}`
            : `/pricing?redirectAfterRegister=${redirect}`;
        router.push(redirectPath);
    }, [category, subscriptionId, pathname, router, dispatchConfirmationModal]);

    const operation = confirmationModal?.operation;
    const calculatedCost = getCreditCost(category, formStates, { operation, submitStates });

    return (
        <ConfirmationModal
            isOpen={isOpen}
            title="Insufficient credits"
            content={getModalContent(category, calculatedCost, credits, operation)}
            confirmLabel="Get Credits"
            cancelLabel="Cancel"
            onConfirm={handleGetCredits}
            onCancel={onClose}
        />
    );
};
